import React, { useEffect, useMemo, useState } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { useApi } from '../contexts/ApiContext';

const buildAsteriskState = (source = {}) => ({
  host: source.host || '',
  port: String(source.port ?? 5038),
  user: source.user || '',
  password: source.password || '',
  ssh: {
    host: source.ssh?.host || source.host || '',
    port: String(source.ssh?.port ?? 22),
    user: source.ssh?.user || '',
    password: source.ssh?.password || '',
    includeFile: source.ssh?.includeFile || '/etc/asterisk/sip_custom.conf',
    sipRegisterFile: source.ssh?.sipRegisterFile || '/etc/asterisk/sip_register.conf',
    extensionsFile: source.ssh?.extensionsFile || '/etc/asterisk/extensions_custom.conf',
    queuesFile: source.ssh?.queuesFile || '/etc/asterisk/queues_custom.conf',
    useSudo: !!source.ssh?.useSudo
  },
  recordings: {
    virtualPath: source.recordings?.virtualPath || '/var/spool/asterisk/monitor/',
    physicalPath: source.recordings?.physicalPath || ''
  },
  softphone: {
    wsUrl: source.softphone?.wsUrl || ''
  }
});

const SettingsPage = () => {
  const { systemConfig } = useApi();
  const { canAction } = usePermissions();
  const [timezones, setTimezones] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [asteriskConfig, setAsteriskConfig] = useState(buildAsteriskState());
  const [asteriskConfigured, setAsteriskConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [savingAsterisk, setSavingAsterisk] = useState(false);
  const [timezoneError, setTimezoneError] = useState('');
  const [timezoneSuccess, setTimezoneSuccess] = useState('');
  const [asteriskError, setAsteriskError] = useState('');
  const [asteriskSuccess, setAsteriskSuccess] = useState('');
  const [recordingsStatus, setRecordingsStatus] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [configResponse, timezonesResponse] = await Promise.all([
          systemConfig.get(),
          systemConfig.getTimezones()
        ]);

        setSelectedTimezone(configResponse.data?.timezone || null);
        setAsteriskConfig(buildAsteriskState({
          ...configResponse.data?.asterisk,
          recordings: configResponse.data?.recordings,
          softphone: configResponse.data?.softphone
        }));
        setAsteriskConfigured(configResponse.data?.status?.asteriskConfigured ?? true);
        setTimezones(timezonesResponse.data || []);
      } catch (err) {
        console.error('[SETTINGS] Error loading configuration', err);
        setTimezoneError(err.message || 'No se pudo cargar la configuración');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [systemConfig]);

  const timezoneOptions = useMemo(() => timezones, [timezones]);

  const handleTimezoneSubmit = async (event) => {
    event.preventDefault();
    if (!selectedTimezone?.id) return;

    try {
      setSavingTimezone(true);
      setTimezoneError('');
      setTimezoneSuccess('');
      const response = await systemConfig.update({ timezone: selectedTimezone.id });
      setSelectedTimezone(response.data?.timezone || selectedTimezone);
      setAsteriskConfigured(response.data?.status?.asteriskConfigured ?? asteriskConfigured);
      setTimezoneSuccess('Zona horaria actualizada correctamente. Reinicia los servicios si sigues viendo horarios incorrectos.');
    } catch (err) {
      setTimezoneError(err.message || 'No se pudo guardar la configuración de zona horaria');
    } finally {
      setSavingTimezone(false);
    }
  };

  const handleAsteriskSubmit = async (event) => {
    event.preventDefault();
    try {
      setSavingAsterisk(true);
      setAsteriskError('');
      setAsteriskSuccess('');
      const asteriskPayload = {
        host: asteriskConfig.host,
        port: Number(asteriskConfig.port),
        user: asteriskConfig.user,
        password: asteriskConfig.password,
        ssh: {
          host: asteriskConfig.ssh.host,
          port: Number(asteriskConfig.ssh.port),
          user: asteriskConfig.ssh.user,
          password: asteriskConfig.ssh.password,
          includeFile: asteriskConfig.ssh.includeFile,
          sipRegisterFile: asteriskConfig.ssh.sipRegisterFile,
          extensionsFile: asteriskConfig.ssh.extensionsFile,
          queuesFile: asteriskConfig.ssh.queuesFile,
          useSudo: asteriskConfig.ssh.useSudo
        }
      };
      const recordingsPayload = {
        virtualPath: asteriskConfig.recordings.virtualPath,
        physicalPath: asteriskConfig.recordings.physicalPath
      };
      const softphonePayload = {
        wsUrl: asteriskConfig.softphone.wsUrl
      };
      const response = await systemConfig.update({
        asterisk: asteriskPayload,
        recordings: recordingsPayload,
        softphone: softphonePayload
      });
      setAsteriskConfig(buildAsteriskState({
        ...response.data?.asterisk || asteriskPayload,
        recordings: response.data?.recordings || recordingsPayload,
        softphone: response.data?.softphone || softphonePayload
      }));
      setAsteriskConfigured(response.data?.status?.asteriskConfigured ?? true);
      setRecordingsStatus(response.data?.status?.recordings || null);
      setAsteriskSuccess('Configuración de Asterisk actualizada correctamente. Reinicia los servicios si necesitas aplicar los cambios de inmediato.');
    } catch (err) {
      setAsteriskError(err.message || 'No se pudo guardar la configuración de Asterisk');
    } finally {
      setSavingAsterisk(false);
    }
  };

  const handleAsteriskFieldChange = (field) => (event) => {
    const value = event.target.value;
    setAsteriskConfig((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSshFieldChange = (field) => (event) => {
    const value = event.target.value;
    setAsteriskConfig((prev) => ({
      ...prev,
      ssh: {
        ...prev.ssh,
        [field]: value
      }
    }));
  };

  const handleSshUseSudoChange = (event) => {
    const checked = event.target.checked;
    setAsteriskConfig((prev) => ({
      ...prev,
      ssh: {
        ...prev.ssh,
        useSudo: checked
      }
    }));
  };

  const handleRecordingsFieldChange = (field) => (event) => {
    const value = event.target.value;
    setAsteriskConfig((prev) => ({
      ...prev,
      recordings: {
        ...prev.recordings,
        [field]: value
      }
    }));
  };

  const handleSoftphoneFieldChange = (field) => (event) => {
    const value = event.target.value;
    setAsteriskConfig((prev) => ({
      ...prev,
      softphone: {
        ...prev.softphone,
        [field]: value
      }
    }));
  };

  const isLoading = loading;

  return (
    <Box component="section">
      <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
        Configuración del Sistema
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Administra parámetros globales del marcador. La zona horaria y la conexión a Asterisk afectan a todos los motores y servicios.
      </Typography>

      {!asteriskConfigured && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No hay una conexión válida con Asterisk. Completa los parámetros de AMI y SSH en esta página para habilitar el marcador.
        </Alert>
      )}

      <Card sx={{ mt: 4 }}>
        <CardContent component="form" onSubmit={handleTimezoneSubmit}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" component="h2">
                Zona horaria
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Selecciona la zona horaria que debe usar el backend para guardar fechas y horas.
              </Typography>
            </Box>

            {timezoneError && <Alert severity="error">{timezoneError}</Alert>}
            {timezoneSuccess && <Alert severity="success">{timezoneSuccess}</Alert>}

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Autocomplete
                options={timezoneOptions}
                value={selectedTimezone}
                onChange={(_, value) => setSelectedTimezone(value)}
                disableClearable
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                getOptionLabel={(option) => option?.label || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Zona horaria"
                    placeholder="Selecciona una zona horaria"
                  />
                )}
              />
            )}

            {canAction('edit_settings') && (
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button
                type="submit"
                variant="contained"
                disabled={savingTimezone || isLoading || !selectedTimezone?.id}
              >
                {savingTimezone ? 'Guardando…' : 'Guardar cambios'}
              </Button>
            </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mt: 4 }}>
        <CardContent component="form" onSubmit={handleAsteriskSubmit}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" component="h2">
                Asterisk / PBX
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configura los parámetros de conexión para AMI y la sincronización de archivos vía SSH.
              </Typography>
            </Box>

            {asteriskError && <Alert severity="error">{asteriskError}</Alert>}
            {asteriskSuccess && <Alert severity="success">{asteriskSuccess}</Alert>}
            {recordingsStatus && (
              <Alert severity={recordingsStatus.errors?.length ? 'warning' : 'success'}>
                {recordingsStatus.errors?.length
                  ? `Grabaciones (${recordingsStatus.mode || 'local'}): ${recordingsStatus.errors.join(' | ')}`
                  : `Grabaciones (${recordingsStatus.mode || 'local'}): ruta ${recordingsStatus.created ? 'creada' : 'verificada'} en ${recordingsStatus.path}`}
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Conexión AMI
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Host / IP"
                    value={asteriskConfig.host}
                    onChange={handleAsteriskFieldChange('host')}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Puerto AMI"
                    type="number"
                    value={asteriskConfig.port}
                    onChange={handleAsteriskFieldChange('port')}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Usuario AMI"
                    value={asteriskConfig.user}
                    onChange={handleAsteriskFieldChange('user')}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Contraseña AMI"
                    type="password"
                    value={asteriskConfig.password}
                    onChange={handleAsteriskFieldChange('password')}
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Sincronización de archivos (SSH)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Host SSH"
                    value={asteriskConfig.ssh.host}
                    onChange={handleSshFieldChange('host')}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Puerto SSH"
                    type="number"
                    value={asteriskConfig.ssh.port}
                    onChange={handleSshFieldChange('port')}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={asteriskConfig.ssh.useSudo}
                        onChange={handleSshUseSudoChange}
                      />
                    }
                    label="Usar sudo"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Usuario SSH"
                    value={asteriskConfig.ssh.user}
                    onChange={handleSshFieldChange('user')}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Contraseña SSH"
                    type="password"
                    value={asteriskConfig.ssh.password}
                    onChange={handleSshFieldChange('password')}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Archivo sip_custom.conf"
                    value={asteriskConfig.ssh.includeFile}
                    onChange={handleSshFieldChange('includeFile')}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Archivo sip_register.conf"
                    value={asteriskConfig.ssh.sipRegisterFile}
                    onChange={handleSshFieldChange('sipRegisterFile')}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Archivo extensions_custom.conf"
                    value={asteriskConfig.ssh.extensionsFile}
                    onChange={handleSshFieldChange('extensionsFile')}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Archivo queues_custom.conf"
                    value={asteriskConfig.ssh.queuesFile}
                    onChange={handleSshFieldChange('queuesFile')}
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Grabaciones PBX
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Configura las rutas donde se almacenan las grabaciones del PBX.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Ruta virtual PBX"
                    value={asteriskConfig.recordings.virtualPath}
                    onChange={handleRecordingsFieldChange('virtualPath')}
                    fullWidth
                    helperText="Ruta virtual donde el PBX guarda las grabaciones en Linux"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Ruta física (Windows)"
                    value={asteriskConfig.recordings.physicalPath}
                    onChange={handleRecordingsFieldChange('physicalPath')}
                    fullWidth
                    helperText="Ruta física del recurso compartido en Windows"
                    required
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Softphone WebSocket
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                URL completa del servidor WebSocket del softphone (ej. wss://pbx.miempresa.com/ws-asterisk). Si se deja vacío, se usará el mismo dominio de esta página con la ruta /ws-asterisk.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="WS Server URL"
                    value={asteriskConfig.softphone.wsUrl}
                    onChange={handleSoftphoneFieldChange('wsUrl')}
                    fullWidth
                    placeholder="wss://pbx.miempresa.com/ws-asterisk"
                  />
                </Grid>
              </Grid>
            </Box>

            {canAction('edit_settings') && (
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button
                type="submit"
                variant="contained"
                disabled={savingAsterisk || isLoading}
              >
                {savingAsterisk ? 'Guardando…' : 'Guardar Asterisk'}
              </Button>
            </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SettingsPage;
